import React from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Typography, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Paper 
} from '@mui/material';
import { 
  CheckCircleOutline, 
  Cake, 
  MenuBook, 
  CalendarMonth, 
  Tune, 
  LocalOffer 
} from '@mui/icons-material';
import PublicLayout from "../../layouts/PublicLayout";

export default function Bakery() {
  return (
    <PublicLayout>
      <Box sx={{ bgcolor: '#fff', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="flex-start">
            
            {/* LEFT CONTENT */}
            <Grid item xs={12} md={7}>
              <Typography variant="overline" sx={{ color: '#f6521a', fontWeight: 800, letterSpacing: 1.5 }}>
                TPOS FOR BAKERIES & PATISSERIES
              </Typography>
              
              <Typography variant="h2" sx={{ fontWeight: 600, mt: 1, mb: 2, fontSize: { xs: '1.5rem', md: '2.5rem' }, lineHeight: 1.1 }}>
                Bake Better Margins with <span style={{ color: "#f6521a" }}>Advanced Order Management.</span>
              </Typography>

              <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 500, mb: 4, lineHeight: 1.5 }}>
                Take control of your raw material tracking, manage complex custom cake orders, and streamline scheduled deliveries effortlessly.
              </Typography>

              <Typography variant="body1" sx={{ color: 'text.primary', mb: 5, fontSize: '1.1rem', lineHeight: 1.8 }}>
                Baking is a precise science, and your business operations should be too. From tracking the exact consumption of flour, sugar, and butter to managing custom, multi-tier birthday cake orders with advance deposits, a standard restaurant POS simply isn't enough. 
                <strong> TPOS provides specialized tools specifically engineered for bakeries</strong>, ensuring your kitchen's daily production planning aligns perfectly with walk-in sales and future advance orders.
              </Typography>

              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                Specialized TPOS Features:
              </Typography>

              <Grid container spacing={3}>
                {[
                  {
                    title: "Advance Order & Deposit Management",
                    desc: "Take orders for next week, record custom cake messages, log partial advance payments, and print scheduled KOTs.",
                    icon: <Cake sx={{ color: '#f6521a' }} />
                  },
                  { 
                    title: "Recipe & Yield Tracking", 
                    desc: "Map every pastry to its raw ingredients. Selling a slice of cake automatically deducts the exact grams of flour and cocoa.",
                    icon: <MenuBook sx={{ color: '#f6521a' }} />
                  },
                  { 
                    title: "Production Planning Calendar", 
                    desc: "Generate daily 'Bake Lists' for your kitchen staff based on upcoming advance orders and historical daily average sales.",
                    icon: <CalendarMonth sx={{ color: '#f6521a' }} />
                  },
                  { 
                    title: "Custom Modifier Engine", 
                    desc: "Easily capture specific customer requests (e.g., 'Eggless', 'Photo Print Icing') directly on the billing screen.",
                    icon: <Tune sx={{ color: '#f6521a' }} />
                  },
                  { 
                    title: "Automated Shelf-Life Discounts", 
                    desc: "Automatically trigger 'Happy Hour' or clearance pricing on perishable items in the last two hours of the day to minimize waste.",
                    icon: <LocalOffer sx={{ color: '#f6521a' }} />
                  }
                ].map((item, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                      <Box sx={{ mt: 0.5 }}>{item.icon}</Box>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{item.title}</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{item.desc}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>

            </Grid>

            {/* RIGHT IMAGE */}
            <Grid item xs={12} md={5} sx={{ position: 'sticky', top: 100 }}>
              <Paper 
                elevation={0} 
                sx={{ 
                  borderRadius: 4, 
                  overflow: 'hidden', 
                  background: 'linear-gradient(145deg, #ffffff 0%, #f9f9f9 100%)',
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%'
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: { xs: 300, md: 550 },
                    borderRadius: 3,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'right',
                    float:'right'                    
                  }}
                >
                  <img 
                    src="/img/BakeryShop.png" 
                    alt="Bakery Management POS" 
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                </Box>
                
                <Box sx={{ p: 3, width: '100%' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Business Impact</Typography>
                  <List dense>
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 35 }}><CheckCircleOutline sx={{ color: '#00ab55', fontSize: 20 }} /></ListItemIcon>
                      <ListItemText primary="Eliminate 'forgotten' advance orders entirely with centralized digital tracking." />
                    </ListItem>
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 35 }}><CheckCircleOutline sx={{ color: '#00ab55', fontSize: 20 }} /></ListItemIcon>
                      <ListItemText primary="Reduce daily food waste by 15% via smart inventory yield mapping and clearance pricing." />
                    </ListItem>
                  </List>
                </Box>
              </Paper>
            </Grid>

          </Grid>
        </Container>
      </Box>
    </PublicLayout>
  );
}